import { useEffect, useMemo, useState } from 'react'
import { Skeleton, Typography } from 'antd'
import { BellOutlined, HistoryOutlined, RightOutlined, TagsOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { DEMO_MENU_GROUPS } from '@/config/demoNavigation'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { usePermission } from '@/hooks/usePermission'
import BusinessReport from '@/pages/report/BusinessReport'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { pageToPath } from '@/routes'
import { dashboardApi } from '@/services/dashboard/api'
import type { AnnouncementResponse } from '@/services/dashboard/types'
import { releaseHistoriesApi } from '@/services/releaseHistories/api'
import type { ReleaseHistoryResponse } from '@/services/releaseHistories/types'
import { DASHBOARD_PERMISSIONS } from '@/constants'

const TopMenu = () => {
  const { t } = useTranslation('nav')
  const { t: tc } = useTranslation('common')
  const navigate = useAppNavigate()
  const routerNavigate = useNavigate()

  const [announcements, setAnnouncements] = useState<AnnouncementResponse[]>([])
  const [announcementsLoading, setAnnouncementsLoading] = useState(true)
  const [releaseHistories, setReleaseHistories] = useState<ReleaseHistoryResponse[]>([])
  const [releaseLoading, setReleaseLoading] = useState(true)
  const { hasAnyPermission } = usePermission()
  // ダッシュボード閲覧権限を持たないユーザーは BusinessReport を描画しない（後端で 403 になるため）。
  const canViewDashboard = hasAnyPermission(...DASHBOARD_PERMISSIONS)

  useEffect(() => {
    dashboardApi.getAnnouncements()
      .then(({ data }) => setAnnouncements(data))
      .catch(() => {})
      .finally(() => setAnnouncementsLoading(false))
  }, [])

  useEffect(() => {
    releaseHistoriesApi.list()
      .then(({ data }) => setReleaseHistories(data))
      .catch(() => {})
      .finally(() => setReleaseLoading(false))
  }, [])

  // 「本日（当日）」発行分のみ全文を直接表示する。過去分はリリース履歴ページで確認。
  const todaysReleases = useMemo(
    () => releaseHistories.filter((r) => r.releasedAt && dayjs(r.releasedAt).isSame(dayjs(), 'day')),
    [releaseHistories],
  )

  // 現在のシステムバージョン = 公開済みリリース履歴（リリース日降順）の先頭レコードのバージョン。
  const latestVersion = useMemo(
    () => releaseHistories.find((r) => r.version?.trim())?.version?.trim() ?? null,
    [releaseHistories],
  )

  const menuGroups = useMemo(
    () =>
      DEMO_MENU_GROUPS
        .filter((group) => group.showOnTopMenu)
        .filter((group) => !group.requiredAnyPermission || hasAnyPermission(...group.requiredAnyPermission))
        .map((group) => ({
          id: group.id,
          title: t(group.titleKey as never) as string,
          icon: group.icon,
          colorClass: group.colorClass,
          target: group.topMenuTarget,
        })),
    [t, hasAnyPermission],
  )

  const announcementsCard = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
          <BellOutlined />
        </span>
        <h3 className="text-base font-bold text-gray-800 m-0">{tc('announcements')}</h3>
      </div>
      <div className="px-5 py-4">
        {announcementsLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : announcements.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <BellOutlined className="text-3xl mb-2" />
            <p className="text-sm m-0">お知らせはありません</p>
          </div>
        ) : (
          <ul className="space-y-3 text-sm text-gray-700 m-0 p-0 list-none">
            {announcements.map((item) => {
              const body = item.body
                ? (
                    <p className="text-gray-600 mt-0.5 mb-0 whitespace-pre-wrap">
                      {item.body.replace(/\\r\\n|\\r|\\n/g, '\n')}
                    </p>
                  )
                : null
              if (item.linkPage) {
                return (
                  <li key={item.id} className="border-l-2 border-blue-400 rounded-r">
                    <button
                      type="button"
                      onClick={() => {
                        const path = pageToPath(item.linkPage as string)
                        const qs = item.linkQuery && Object.keys(item.linkQuery).length > 0
                          ? '?' + new URLSearchParams(item.linkQuery).toString()
                          : ''
                        routerNavigate(path + qs)
                      }}
                      aria-label={`${item.title} の詳細へ`}
                      className="block w-full text-left pl-3 pr-2 py-1 hover:bg-blue-50 rounded-r transition cursor-pointer"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-gray-800">{item.title}</span>
                        <RightOutlined className="text-gray-400 text-xs flex-shrink-0" />
                      </div>
                      {body}
                    </button>
                  </li>
                )
              }
              return (
                <li
                  key={item.id}
                  className="border-l-2 border-blue-400 pl-3 py-1 hover:bg-blue-50/40 rounded-r transition"
                >
                  <div className="font-medium text-gray-800">{item.title}</div>
                  {body}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )

  // 本日の更新（当日発行分）を全文表示し、過去分はリリース履歴ページへ誘導するカード。
  const releaseHistoryCard = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-3 bg-gradient-to-r from-blue-50 to-white border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
            <HistoryOutlined />
          </span>
          <h3 className="text-base font-bold text-gray-800 m-0">
            {t('groups.admin.items.releaseHistory.title' as never) as string}
          </h3>
        </div>
        <button
          type="button"
          onClick={() => navigate('release-history')}
          aria-label="過去の更新履歴を開く"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 transition cursor-pointer"
        >
          過去の更新履歴
          <RightOutlined className="text-xs" />
        </button>
      </div>
      <div className="px-5 py-4">
        {releaseLoading ? (
          <Skeleton active paragraph={{ rows: 2 }} />
        ) : todaysReleases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 text-gray-400">
            <HistoryOutlined className="text-3xl mb-2" />
            <p className="text-sm m-0">本日の更新はありません</p>
          </div>
        ) : (
          <ul className="space-y-4 text-sm text-gray-700 m-0 p-0 list-none">
            {todaysReleases.map((item) => (
              <li key={item.id} className="border-l-2 border-blue-400 pl-3">
                <div className="flex flex-wrap items-center gap-2">
                  {item.version ? (
                    <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
                      {item.version}
                    </span>
                  ) : null}
                  <span className="font-semibold text-gray-800">{item.title || 'リリース'}</span>
                </div>
                {item.body ? (
                  <p className="mt-1 mb-0 whitespace-pre-wrap leading-7 text-gray-600">
                    {item.body}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[]} />
      <main className="mx-auto w-full max-w-[1600px] px-6 py-4 sm:py-6 lg:py-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
          <div className="min-w-0 space-y-6">
            {canViewDashboard ? <BusinessReport embedded /> : null}
            {announcementsCard}
            {/* お知らせとは別の「リリース履歴（更新履歴）」。本日発行分を全文表示し、過去分は履歴ページへ。 */}
            {releaseHistoryCard}
          </div>
          <aside className="flex flex-col gap-4 lg:sticky lg:top-6">
            <div className="mb-1 shrink-0">
              <Typography.Title level={4} className="!mb-1">
                {tc('businessMenu')}
              </Typography.Title>
              <Typography.Text type="secondary">{tc('selectTask')}</Typography.Text>
            </div>
            {menuGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => navigate(group.target, { resetSearchPersistence: true })}
                className="group w-full text-left bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 hover:border-blue-300 cursor-pointer"
              >
                <div className="flex items-center space-x-4">
                  <div className={`w-14 h-14 flex-shrink-0 bg-gradient-to-br ${group.colorClass} rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform`}>
                    <span className="text-2xl">{group.icon}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                      {group.title}
                    </h3>
                  </div>
                </div>
              </button>
            ))}
          </aside>
        </div>
      </main>

      {/* 画面右下に現在のシステムバージョン（最新リリース履歴のバージョン）を常時表示。クリックでリリース履歴へ。 */}
      {latestVersion ? (
        <button
          type="button"
          onClick={() => navigate('release-history')}
          title="リリース履歴を開く"
          aria-label={`現在のバージョン ${latestVersion}。リリース履歴を開く`}
          className="fixed bottom-4 right-4 z-40 inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-gray-200 bg-white/90 px-3.5 py-1.5 text-xs font-medium text-gray-500 shadow-md backdrop-blur transition hover:border-blue-300 hover:text-blue-600 hover:shadow-lg"
        >
          <TagsOutlined aria-hidden className="text-blue-500" />
          <span className="tracking-wide">{latestVersion}</span>
        </button>
      ) : null}
    </div>
  )
}

export default TopMenu
